import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';

// Vite can't resolve a runtime template-literal `import()`, so we statically
// enumerate every generated HTML doc up front. Each value is a lazy loader that
// returns the file's raw contents. Keys look like `./rezi-api-docs.html`.
const htmlDocs = import.meta.glob('./*.html', {
  query: '?raw',
  import: 'default',
}) as Record<string, () => Promise<string>>;

const StaticDoc = ({ fileName }: { fileName: string }) => {
  const [fileContent, setFileContent] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        const loader = htmlDocs[`${fileName}.html`];
        if (!loader) {
          setFileContent('');
          return;
        }
        setFileContent(await loader());
      } catch (err) {
        console.error('Failed to load HTML doc:', err);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [fileName]);

  useEffect(() => {
    const mainContainer = document.querySelector('main');
    const docSearchBar = document.getElementById('doc-search-bar');

    // Save the original classes so we can restore them perfectly
    const originalMainClass = mainContainer?.className || '';

    // 1. Apply "Fullscreen" state
    if (docSearchBar) {
      docSearchBar.classList.add('hidden');
    }
    if (mainContainer) {
      // Wipe out the max-w-6xl, px-6, etc.
      mainContainer.className = 'w-full m-0 p-0';
    }

    return () => {
      // 2. CLEANUP: Restore the original class strings exactly as they were
      if (docSearchBar) {
        docSearchBar.classList.remove('hidden');
      }
      if (mainContainer) {
        mainContainer.className = originalMainClass;
      }
    };
  }, []);

  if (loading) return <div>Loading Documentation...</div>;
  if (!fileContent) return <div>Document not found.</div>;

  return (
    /* Use flex-col and h-screen to lock the container to the viewport height */
    <div className="relative right-1/2 left-1/2 -mr-[50vw] -ml-[50vw] flex h-screen w-screen flex-col bg-white">
      {/* The Header/Nav: sticky and z-index to stay on top */}
      <div className="sticky top-0 z-20 w-full border-b border-gray-100 bg-white px-4 py-3 shadow-sm">
        <nav className="flex items-center text-sm text-gray-500">
          <Link
            className="text-rezi-blue-600 font-semibold hover:underline"
            to="/"
          >
            Docs
          </Link>
          <span className="px-2 text-gray-400">/</span>
          <span className="font-medium text-gray-700">
            {fileName.replaceAll('./', '')}
          </span>
        </nav>
      </div>

      {/* The Iframe: flex-1 ensures it takes up all remaining space below the nav */}
      <iframe
        title="API Docs"
        srcDoc={fileContent}
        className="w-full flex-1 border-none"
        style={{ height: '100%' }} // Inside flex-1, height 100% fills the container
      />
    </div>
  );
};

export default StaticDoc;
